import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { redirect } from 'react-router';

import { fetchCountries } from '@/actions/country';
import { fetchSectors } from '@/actions/sector';
import { track } from '@/actions/tracking';
// TODO: Once OTP flow is validated, rename NewCustomerSignupModalOTP to NewCustomerSignupModal
// and delete the old component
import { NewCustomerSignupModalOTP as NewCustomerSignupModal } from '@/auth/components/NewCustomerSignupModal';
import type {
  AuthenticatedViewerForRouter,
  Expert,
  SchedulingData,
} from '@/auth/components/NewCustomerSignupModal/types';
import { SMBSignupModal } from '@/auth/components/SMBSignupModal';
import ConsultationRequestRouter from '@/components/ConsultationRequestRouter';
import Layout from '@/components/Layout';
import TeamAccountPromoDialog from '@/components/TeamAccountPromo/TeamAccountPromoDialog';
import { hasErrorCode } from '@/core/api';
import ERROR_CODES from '@/core/apiErrorCodes';
import { resolveScopedBillingGroup } from '@/core/user';
import { Viewer } from '@/core/viewer';
import { ExpertRequest } from '@/expertrequest';
import { fetchExpertRequest } from '@/expertrequest/store';
import { requestConsultationDisabled } from '@/expertrequest/utils/consultationGate';
import { channelUrlToId } from '@/messaging/sendbird';
import { createChannel } from '@/messaging/store';
import NotFoundPage from '@/pages/NotFoundPage';
import Profile from '@/profile/components/Profile';
import { fetchProfileRatesForGroup } from '@/profile/store';
import { ActionContext, LegacyRoute } from '@/routes/routesMiddleware';
import { AppStore } from '@/store';
import { fetchUser } from '@/store/user';
import { clearCache, getCache, isBot, isValidSMBUtmSource, rateDisplay } from '@/utils';

const CREATE_CHANNEL_AUTH_ERROR = 'GraphQL Error: basic accounts cannot create messaging channels';

/**
 * Builds authenticatedViewer object from viewer data
 * Returns undefined for anonymous users
 */
function buildAuthenticatedViewer(viewer: Viewer): AuthenticatedViewerForRouter | undefined {
  // Anonymous users return undefined
  if (!viewer.id) {
    return undefined;
  }

  const group = viewer.groups?.[0];
  const billingAccount = group?.billing_account;

  return {
    id: viewer.id,
    timezone: viewer.profile?.timezone || null,
    groupName: group?.name || null,
    groupId: group?.id,
    hasBillingAccount: !!billingAccount,
    profileName: viewer.profile?.name || null,
  };
}

// Client-side component that handles Phase 1 SMB signup modal for unlogged users
interface SMBModalWrapperProps {
  profileSlug: string;
  requestConsultation: boolean;
  returnTo?: string;
  expert: {
    name: string;
    title: string;
    pictureUrl?: string;
  };
}

function SMBModalWrapper({
  profileSlug,
  requestConsultation,
  returnTo,
  expert,
}: SMBModalWrapperProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isClient, setIsClient] = useState(false);

  // On mount, check if we should show the modal
  useEffect(() => {
    setIsClient(true);

    // Phase 1 modal is non-dismissable via backdrop/ESC (enforced in SMBSignupModal),
    // but can be closed programmatically after successful signup
    setIsOpen(true);
  }, [profileSlug, requestConsultation]);

  // Don't render anything on server or if modal is closed
  if (!isClient || !isOpen) {
    return null;
  }

  return (
    <SMBSignupModal
      open={true}
      expert={expert}
      returnTo={returnTo}
      // After successful signup, user is logged in and modal closes automatically
      onClose={() => {
        setIsOpen(false);
      }}
    />
  );
}

// Client-side component that handles Phase 2 modal for returning users
interface Phase2ModalWrapperProps {
  expert: Expert;
  landingUrl: string | undefined;
  initialScreen?: 'booking' | 'payment-existing';
  initialSchedulingData?: SchedulingData;
  authenticatedViewer?: AuthenticatedViewerForRouter;
  onClose: () => void;
}

function Phase2ModalWrapper({
  expert,
  landingUrl,
  initialScreen,
  initialSchedulingData,
  authenticatedViewer,
  onClose,
}: Phase2ModalWrapperProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    onClose(); // Call parent's onClose to update URL
  };

  const handleComplete = () => {
    // After successful Phase 2 completion (billing setup), redirect to consultations page
    if (typeof window !== 'undefined') {
      window.location.href = '/consultations';
    }
  };

  // Don't render anything on server or if modal is closed
  if (!isClient || !isOpen) {
    return null;
  }

  return (
    <NewCustomerSignupModal
      open={true}
      onClose={handleClose}
      onComplete={handleComplete}
      expert={expert}
      landingUrl={landingUrl}
      initialScreen={initialScreen}
      initialSchedulingData={initialSchedulingData}
      authenticatedViewer={authenticatedViewer}
    />
  );
}

// Container that manages modal key for remounting without page reload
function Phase2ModalContainer({
  expert,
  landingUrl,
  initialScreen,
  initialSchedulingData,
  authenticatedViewer,
  onClose,
}: Phase2ModalWrapperProps) {
  const [modalKey, setModalKey] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Reset dismissed state when navigating back to /request
  useEffect(() => {
    const checkNavigation = () => {
      if (window.location.pathname.endsWith('/request') && isDismissed) {
        setIsDismissed(false);
      }
    };

    window.addEventListener('popstate', checkNavigation);

    // Also intercept replaceState/pushState since we use history.replaceState
    const originalReplaceState = history.replaceState.bind(history);
    const originalPushState = history.pushState.bind(history);
    history.replaceState = (...args) => {
      originalReplaceState(...args);
      checkNavigation();
    };
    history.pushState = (...args) => {
      originalPushState(...args);
      checkNavigation();
    };

    return () => {
      window.removeEventListener('popstate', checkNavigation);
      history.replaceState = originalReplaceState;
      history.pushState = originalPushState;
    };
  }, [isDismissed]);

  // Don't render on server or if dismissed
  if (!isClient || isDismissed) {
    return null;
  }

  const handleClose = () => {
    setIsDismissed(true); // Prevents immediate remount
    setModalKey((prev) => prev + 1); // Ready for next open
    onClose(); // Update URL and preserve UTM params
  };

  return (
    <Phase2ModalWrapper
      key={modalKey}
      expert={expert}
      landingUrl={landingUrl}
      initialScreen={initialScreen}
      initialSchedulingData={initialSchedulingData}
      authenticatedViewer={authenticatedViewer}
      onClose={handleClose}
    />
  );
}

interface LinkedInData {
  '@context': string;
  '@type': string;
  name: string;
  url: string;
  jobTitle: string;
  address: {
    '@type': string;
    addressCountry: string;
    addressLocality: string;
  };
  worksFor: {
    '@type': string;
    legalName: string;
    address: string;
  };
}

export function getLinkedData(profile: Record<string, any>): LinkedInData {
  const exp = profile.experiences && profile.experiences.find((e: any) => e.current);
  return {
    '@context': 'http://schema.org',
    '@type': 'Person',
    name: profile.name,
    url: profile.html_url,
    jobTitle: profile.title,
    address: {
      '@type': 'PostalAddress',
      addressCountry: profile.country,
      addressLocality: profile.city,
    },
    worksFor: exp && {
      '@type': 'Organization',
      legalName: exp.company_name,
      address: exp.region,
    },
  };
}

function trackProfileView(store: AppStore, viewer: Viewer, user: any) {
  const { profile } = user;

  const isSelf = viewer.id === user.id;
  if (isSelf) return;

  if (viewer.id) {
    store.dispatch(track('profile.view', profile.id, undefined, true));
  }

  store.dispatch(track('profile.view', profile.id, { ua: viewer.userAgentParsed.ua }, true));
}

interface ActionOptions {
  requestConsultation?: boolean;
  messagingPromo?: boolean;
}

async function action(
  { params, store, query, permission }: ActionContext,
  options: ActionOptions = {}
): Promise<JSX.Element | Response> {
  const { requestConsultation = false, messagingPromo = false } = options;
  const { username } = params;
  const expertRequestId = query.get('expertRequestId') || undefined;
  const openedDialog = !!query.get('dialog');
  // Check for SMB modal restoration after email/password login
  const smbModal = query.get('smbModal');
  // Check if URL has valid utm_source param (came from partner site like GovTribe)
  const utmSource = query.get('utm_source');
  const hasPartnerParams = isValidSMBUtmSource(utmSource);

  // Build returnTo search string preserving query params (e.g., expertRequestId)
  const returnToSearch = query.toString() ? `?${query.toString()}` : '';

  const { viewer } = store.getState();
  // ENG-5506: a request launched for a team (the network-invite notification's "Request
  // Expert") is billed and priced for that team when the viewer can bill it. An expert
  // request's account wins over the team in the router, so it never prices for one.
  const scopedGroupId =
    requestConsultation && !expertRequestId
      ? resolveScopedBillingGroup(viewer, query.get('groupId'))?.id
      : undefined;

  if (!username) {
    if (!viewer.html_url) return <NotFoundPage />;
    return redirect(viewer.html_url);
  }

  // Anonymous users with requestConsultation are handled in the JSX below
  // (either SMB modal for utm_source or TeamAccountPromoDialog for others)

  let user;
  try {
    [user] = await Promise.all([
      store.dispatch(
        fetchUser(params.username, {
          stats: true,
          groups: true,
          recruiter: true,
          audit: true,
          experiences: true,
          education: true,
          addresses: true,
          expertise: true,
          groupKeywords: true,
          sources: true,
          internalNetworks: true,
          otpAuthEnabled: !!viewer.admin,
          availableConsultation: true,
        })
      ),
      store.dispatch(fetchCountries()),
      store.dispatch(fetchSectors()),
    ]);
  } catch (e: unknown) {
    if (hasErrorCode(e, ERROR_CODES.AUTH_USER_NOT_FOUND)) {
      return <NotFoundPage />;
    }
    throw e;
  }

  const self = viewer.id === user.id;
  const { profile } = user;
  let scopedRates;
  if (scopedGroupId) {
    try {
      scopedRates = await store.dispatch(fetchProfileRatesForGroup(profile.id, scopedGroupId));
      if (!scopedRates) {
        // The profile itself just loaded, so a missing one here is unexpected, and it drops
        // the scope just as a throw does.
        Sentry.captureMessage('Team-scoped profile rates came back empty', {
          level: 'warning',
          tags: { area: 'billing', reason: 'scoped_rates_empty' },
          // Opaque ids only — no profile or team data.
          extra: { profileId: profile.id, groupId: scopedGroupId },
        });
      }
    } catch (e: unknown) {
      // Reported, not a breadcrumb: the viewer can bill this team and a rate the API can't
      // price resolves to null, so a throw here is a real failure that moves the billed team.
      Sentry.captureException(e);
    }
  }
  // Without the team's rates the scope is dropped entirely, so the price and the billed
  // team fall back to the active account together.
  const bookingGroupId = scopedRates ? scopedGroupId : undefined;
  const bookingProfile = scopedRates
    ? { ...profile, credit_rate: scopedRates.credit_rate, client_rate: scopedRates.client_rate }
    : profile;

  const [updatePerm] = await permission.allowedBatch([
    { service: 'profile', action: 'update', resource: profile.id },
    {
      service: 'group_member',
      action: 'update_group_keywords',
      resource: profile.id,
    },
    { service: 'promo', action: 'see_start_chat', resource: user.id },
    {
      service: 'messaging',
      action: 'start_non_anonymous_chat',
      resource: user.id,
    },
  ]);

  let expertRequest: ExpertRequest | undefined;
  if (expertRequestId) {
    try {
      expertRequest = await store.dispatch(fetchExpertRequest(expertRequestId));
    } catch (e: unknown) {
      if (
        !hasErrorCode(e, ERROR_CODES.EXPERT_REQUEST_NOT_FOUND) &&
        !hasErrorCode(e, ERROR_CODES.PERMISSION_NOT_ALLOWED)
      ) {
        throw e;
      }
      // ENG-5490: the ER fetch was swallowed, so the request-call form falls back to
      // viewer-based currency instead of the ER's account. Leave a breadcrumb so this
      // silent fallback is visible in Sentry — a PERMISSION_NOT_ALLOWED here is a real
      // signal (the user reached this via an ER-scoped CTA that implied access).
      const permissionDenied = hasErrorCode(e, ERROR_CODES.PERMISSION_NOT_ALLOWED);
      Sentry.addBreadcrumb({
        category: 'billing',
        level: 'warning',
        message: 'expert request fetch swallowed; consultation currency falls back to viewer',
        data: { expertRequestId, permissionDenied },
      });
      console.warn(
        permissionDenied ? 'Expert request access denied' : 'Expert request not found',
        expertRequestId
      );
    }
  }

  // Grey out the request-call CTA when the profile is opened from a non-active ER
  // (ENG-5318). Undefined ER (no id, or a not-found/forbidden fetch) leaves it enabled.
  const requestConsultationDisabledValue = requestConsultationDisabled(expertRequest);

  const bot = isBot(viewer.userAgentParsed);
  const anonymousUser = !viewer.id && !bot;

  // Check for rate display cookie to determine if user has signed up before
  const hasRateDisplayCookie = rateDisplay.has();

  // R-9a: Logged-in SMB mid-flow user clicks Request consultation → send
  // them to the dashboard. The dashboard's SetupPausedBanner is the
  // canonical "you need to finish setup" surface with the modal wired in;
  // landing on a blurred expert-profile page with the modal on top would
  // tell them "consult this expert" while the modal says "complete signup
  // first" — mixed signals.
  const hasTeamBilling = !!viewer.groups?.some((g) => !!g?.billing_account);
  if (requestConsultation && viewer.id && viewer.signup_type === 'smb' && !hasTeamBilling) {
    return redirect('/dashboard');
  }

  // Anonymous visitors on expert profiles always route through the partner
  // signup modals (Phase 1 / Phase 2). The partner modals include
  // consultation-request awareness, trial messaging, and the rate-display
  // cookie handshake — the self-service modal does not. The self-service
  // flow has its own dedicated entry at /signup/customer.
  //
  // Check if logged-in user has billing account
  const billingAccount = viewer.groups?.[0]?.billing_account;
  const hasBillingAccount = !!billingAccount;

  // A booking scoped to a team the viewer can bill opens regardless of their first team.
  const isSMBWithoutBilling =
    !bookingGroupId && viewer.id && viewer.signup_type === 'smb' && !hasBillingAccount;

  // Phase 1 modal: anonymous, no rate-display cookie (first-visit cohort).
  const showPhase1Modal = anonymousUser && !hasRateDisplayCookie;

  // Phase 2 modal: anonymous with rate cookie (returning partner customers).
  const showPhase2Modal = anonymousUser && hasRateDisplayCookie && requestConsultation;

  // Check for SMB modal restoration after email/password login
  // smbModal=priceSummary indicates returning user who just logged in via email/password
  const showSMBModalForLoggedInUser = viewer.id && smbModal === 'priceSummary';
  let smbConsultationData: { duration?: string; dates?: string[]; description?: string } | null =
    null;

  if (showSMBModalForLoggedInUser && typeof window !== 'undefined') {
    // Get saved consultation data from localStorage
    smbConsultationData = getCache('smb_consultation_data');
    // Clear it after reading (one-time use)
    clearCache('smb_consultation_data');
    // Clean up URL by removing smbModal param
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('smbModal');
    window.history.replaceState({}, '', cleanUrl.toString());
  }

  // must await to add tracking response to the redux store and prevent
  // client/server duplication, and ignore bots
  if (!bot) {
    await trackProfileView(store, viewer, user);
  }

  // const linkedData = getLinkedData(profile);

  document.title = `${profile.name} — OnFrontiers Expert`;
  return (
    <Layout showNav selected="profile">
      <Profile
        userId={user.id}
        profileId={profile.id}
        editable={updatePerm.allowed}
        self={self}
        expertRequestId={expertRequestId}
        requestConsultationDisabled={requestConsultationDisabledValue}
        openedDialog={openedDialog}
        messagingPromo={messagingPromo}
      />
      {/* ConsultationRequestRouter for logged-in users with billing (exclude SMB without billing) */}
      {viewer.id && requestConsultation && !isSMBWithoutBilling && (
        <ConsultationRequestRouter
          open={true}
          expert={{
            id: user.id,
            name: profile.name,
            slug: profile.url_endpoint,
            title: profile.title,
            pictureUrl: profile.picture_url,
            timezone: profile.timezone,
            clientRate: bookingProfile.client_rate,
            creditRate: bookingProfile.credit_rate,
          }}
          profile={bookingProfile}
          expertId={user.id}
          expertRequest={expertRequest}
          groupId={bookingGroupId}
          returnTo={`/profile/${profile.url_endpoint}${returnToSearch}`}
        />
      )}
      {/* Phase 1: Show simple signup modal for new anonymous users (no cookie) */}
      {showPhase1Modal && (
        <SMBModalWrapper
          profileSlug={profile.url_endpoint}
          requestConsultation={requestConsultation}
          returnTo={`/profile/${profile.url_endpoint}${returnToSearch}`}
          expert={{
            name: profile.name,
            title: profile.title,
            pictureUrl: profile.picture_url,
          }}
        />
      )}
      {/* Phase 2: Show OTP login/billing modal for returning users (has cookie) */}
      {showPhase2Modal && (
        <Phase2ModalContainer
          expert={{
            id: user.id,
            slug: profile.url_endpoint,
            name: profile.name,
            title: profile.title,
            pictureUrl: profile.picture_url,
            city: profile.city,
            country: profile.country,
            timezone: profile.timezone,
            clientRate: profile.client_rate,
            creditRate: profile.credit_rate,
          }}
          landingUrl={typeof window !== 'undefined' ? window.location.href : undefined}
          authenticatedViewer={buildAuthenticatedViewer(viewer)}
          onClose={() => {
            // Update URL without page reload - container manages remounting via key
            const currentSearch = window.location.search;
            window.history.replaceState({}, '', `/profile/${profile.url_endpoint}${currentSearch}`);
          }}
        />
      )}
      {/* Show "Contact Sales" dialog for anonymous users without utm_source trying to request consultation */}
      {anonymousUser &&
        !hasPartnerParams &&
        requestConsultation &&
        !showPhase1Modal &&
        !showPhase2Modal && (
          <TeamAccountPromoDialog
            open={true}
            onClose={() => {
              // Preserve query params when closing dialog
              const currentSearch = window.location.search;
              window.history.replaceState(
                {},
                '',
                `/profile/${profile.url_endpoint}${currentSearch}`
              );
            }}
            showEventTrack="promo.show.team_account.anonymous_request"
          />
        )}
      {/* Show "Contact Sales" dialog for users without messaging permissions (not covered by SMB modal) */}
      {messagingPromo && !showPhase1Modal && !showPhase2Modal && (
        <TeamAccountPromoDialog
          open={true}
          onClose={() => {
            // Preserve query params when closing dialog
            const currentSearch = window.location.search;
            window.history.replaceState({}, '', `/profile/${profile.url_endpoint}${currentSearch}`);
          }}
          showEventTrack="promo.show.team_account.messaging"
        />
      )}
      {/* Show modal for logged-in users after email/password login redirect */}
      {showSMBModalForLoggedInUser && (
        <Phase2ModalContainer
          expert={{
            id: user.id,
            slug: profile.url_endpoint,
            name: profile.name,
            title: profile.title,
            pictureUrl: profile.picture_url,
            city: profile.city,
            country: profile.country,
            timezone: profile.timezone,
            clientRate: profile.client_rate,
            creditRate: profile.credit_rate,
          }}
          landingUrl={typeof window !== 'undefined' ? window.location.href : undefined}
          authenticatedViewer={buildAuthenticatedViewer(viewer)}
          initialScreen="payment-existing"
          initialSchedulingData={
            smbConsultationData
              ? {
                  // Ensure duration is a string (old localStorage data might have number)
                  duration:
                    typeof smbConsultationData.duration === 'string'
                      ? smbConsultationData.duration
                      : '1h',
                  dates: smbConsultationData.dates || [],
                  description: smbConsultationData.description || '',
                }
              : undefined
          }
          onClose={() => {
            // Update URL without page reload - container manages remounting via key
            const currentSearch = window.location.search;
            window.history.replaceState({}, '', `/profile/${profile.url_endpoint}${currentSearch}`);
          }}
        />
      )}
    </Layout>
  );
}

const path = '/profile';

const route: LegacyRoute = {
  path: '/profile',
  children: [
    {
      path: `${path}/:username?`,
      async action(context: ActionContext): Promise<JSX.Element | Response> {
        return await action(context, { requestConsultation: false });
      },
    },
    {
      path: `${path}/:username/request`,
      async action(context: ActionContext): Promise<JSX.Element | Response> {
        return await action(context, { requestConsultation: true });
      },
    },
    {
      path: `${path}/:username/message`,
      async action(context: ActionContext): Promise<JSX.Element | Response> {
        const { store, params, query } = context;
        const { viewer } = store.getState() as { viewer: Viewer };
        const { username } = params;

        // Anonymous users see profile page with TeamAccountPromoDialog
        if (!viewer.id) {
          return await action(context, { messagingPromo: true });
        }

        // Users without billing - route based on signup type
        const billingAccount = viewer.groups?.[0]?.billing_account;
        if (!billingAccount) {
          // SMB users → redirect to request page, preserving query params (UTM tracking)
          if (viewer.signup_type === 'smb') {
            const queryString = Array.from(query.entries())
              .map(
                ([key, value]: [string, string]) =>
                  `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
              )
              .join('&');
            return redirect(`/profile/${username}/request${queryString ? `?${queryString}` : ''}`);
          }
          // Non-SMB users → Contact Sales dialog
          return await action(context, { messagingPromo: true });
        }

        const user = await store.dispatch(fetchUser(username));

        try {
          const channel = await store.dispatch(createChannel([viewer.id, user.id]));
          return redirect(`/messaging/${channelUrlToId(channel.url)}`);
        } catch (err: any) {
          if (err.message === CREATE_CHANNEL_AUTH_ERROR) {
            return await action(context, { messagingPromo: true });
          }
          throw err;
        }
      },
    },
  ],
};

export default route;
